import { Component, OnDestroy, OnInit } from '@angular/core';
import { getDownloadURL, ref, Storage, uploadBytesResumable } from '@angular/fire/storage';
import {
    ControlContainer,
    FormArray,
    FormControl,
    FormGroup,
    FormGroupDirective,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { pruneValue } from '../../../../shared/utils/prune-value.util';
import { AppFeaturesForm, AppForm, FooterSectionFormControls } from '../../models/app-form';

@Component({
    selector: 'app-footer-section',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatSlideToggleModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatListModule,
        MatDividerModule,
        MatProgressSpinnerModule,
        MatOptionModule,
        MatFormFieldModule,
        MatSelectModule,
    ],
    templateUrl: './footer-section-component.component.html',
    styleUrls: ['../../form-app.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class FooterSectionComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();
    public isUploadingFooterLogo = false;

    constructor(
        private parent: FormGroupDirective,
        private storage: Storage,
        private snackBar: MatSnackBar
    ) {}

    ngOnInit(): void {
        const features = this.featuresFormGroup;
        if (features) {
            if (!features.controls.footerSections) {
                features.setControl('footerSections', new FormArray<FormGroup<FooterSectionFormControls>>([]));
            }
            if (!features.controls.footerCompanyLogo) {
                features.setControl('footerCompanyLogo', new FormControl<string | null>(''));
            }
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    get parentForm(): FormGroup<AppForm> {
        return this.parent.form as FormGroup<AppForm>;
    }

    get featuresFormGroup(): FormGroup<AppFeaturesForm> {
        const pf = this.parentForm;
        if (!pf) throw new Error('FooterSectionComponent must be used inside a FormGroupDirective with a parent form');
        const features = pf.controls.features;
        if (!features) throw new Error('features FormGroup is missing on parent form');
        return features;
    }

    get showFooterControl(): FormControl<boolean | null> {
        return this.featuresFormGroup.controls.showFooter;
    }

    get footerCompanyLogoControl(): FormControl<string | undefined | null> {
        return this.featuresFormGroup!.controls.footerCompanyLogo as FormControl<string | undefined | null>;
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        const storagePath = `footer-logos/${file.name}`;
        const fileRef = ref(this.storage, storagePath);
        this.isUploadingFooterLogo = true;
        const uploadTask = uploadBytesResumable(fileRef, file);
        uploadTask
            .then(() =>
                getDownloadURL(fileRef).then((url) => {
                    this.footerCompanyLogoControl?.setValue(url);
                    this.isUploadingFooterLogo = false;
                })
            )
            .catch(() => {
                this.isUploadingFooterLogo = false;
                this.snackBar.open('File upload failed', 'Close', { duration: 3000 });
            });
    }

    get footerSectionsArray(): FormArray<FormGroup<FooterSectionFormControls>> | undefined {
        return this.featuresFormGroup.controls.footerSections;
    }

    public getSectionTitleControl(section: FormGroup<FooterSectionFormControls>): FormControl<string> {
        return section.controls.title;
    }

    public getSectionElementsArray(
        section: FormGroup<FooterSectionFormControls>
    ): FormArray<FormGroup<{ type: FormControl<string>; message: FormControl<string> }>> {
        return section.controls.elements as FormArray<
            FormGroup<{ type: FormControl<string>; message: FormControl<string> }>
        >;
    }

    addSection(): void {
        const section = new FormGroup<FooterSectionFormControls>({
            title: new FormControl('', {
                nonNullable: true,
                validators: Validators.required,
            }),
            elements: new FormArray([
                new FormGroup({
                    type: new FormControl('text', { nonNullable: true }),
                    message: new FormControl('', { nonNullable: true }),
                }),
            ]),
        });

        this.footerSectionsArray?.push(section);
    }

    removeSection(index: number): void {
        this.footerSectionsArray?.removeAt(index);
    }

    addElement(sectionIndex: number): void {
        const sectionGroup = this.footerSectionsArray?.at(sectionIndex);
        const elementsArr = sectionGroup ? this.getSectionElementsArray(sectionGroup) : undefined;
        elementsArr?.push(
            new FormGroup({
                type: new FormControl('text', { nonNullable: true }),
                message: new FormControl('', { nonNullable: true }),
            })
        );
    }

    removeElement(sectionIndex: number, elementIndex: number): void {
        const sectionGroup = this.footerSectionsArray?.at(sectionIndex);
        if (sectionGroup) {
            const arr = this.getSectionElementsArray(sectionGroup);
            arr.removeAt(elementIndex);
        }
    }

    // Normalize footer feature values before saving so we don't send `undefined` to Firestore
    public normalizeFooterForSave(features: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
        if (!features) return features;

        const feature: Record<string, unknown> = { ...features };

        // Ensure footerCompanyLogo is null instead of undefined when empty
        const footerLogo = feature['footerCompanyLogo'];
        if (footerLogo === undefined || (typeof footerLogo === 'string' && !footerLogo.trim())) {
            feature['footerCompanyLogo'] = null;
        }

        // Prune footerSections to remove empty titles/elements (reuse shared pruneValue to strip empties)
        if (feature['footerSections']) {
            const pruned = pruneValue(feature['footerSections']);
            if (pruned && Array.isArray(pruned)) {
                const cleaned = (pruned as unknown[])
                    .map((s) => {
                        if (!s || typeof s !== 'object') return undefined;
                        const sec = s as Record<string, unknown>;
                        const title = sec['title'];
                        const rawElements = sec['elements'];
                        const elements = Array.isArray(rawElements)
                            ? rawElements.filter((e) => (typeof e === 'string' ? e.trim() !== '' : !!e))
                            : [];
                        const out: Record<string, unknown> = {};
                        if (typeof title === 'string' && title.trim()) out['title'] = title.trim();
                        if (elements.length) out['elements'] = elements;
                        return Object.keys(out).length ? out : undefined;
                    })
                    .filter((x): x is Record<string, unknown> => x !== undefined);

                feature['footerSections'] = cleaned;
            } else {
                feature['footerSections'] = [];
            }
        }

        return feature;
    }
}
